import styled from "styled-components";
import { useState } from "react";
import { Check, X, AlertTriangle, Mail, Phone, Wallet } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";

// Killing a payout link that CURRENTLY WORKS and issuing a new one. This is the one front-desk action
// that destroys something functional, so the dialog is built to be read, not clicked through:
//
//   - it states the three things that will happen, in order, before any confirmation is possible
//   - it shows the email the new secret code will be sent to, because a wrong email is the reason
//     these cases exist at all — reissuing to the same broken address just repeats the problem
//   - an OTA relay address is called out by name, since those silently never reach the guest
//   - the confirm button stays disabled until the operator ticks that they checked the address
//
// The tick is not bureaucracy: without it the fastest path through this dialog is the one that
// recreates the exact failure it exists to fix.
// 1500 because this dialog is opened from INSIDE the booking drawer, whose panel sits at 1400. The
// page-level dialogs (ConfirmDialog, BlockRoomDialog) sit at 1200 and must not be copied here: at
// anything below 1400 the drawer covers the right half of this card, including the confirm button.
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.6);
`;

const Card = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 92vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 14px;
  padding: 1.25rem 1.25rem 1rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
`;

const Title = styled.h3`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  font-size: 1.1rem;
  font-weight: 800;
  gap: 0.45rem;
  margin: 0 0 0.6rem;
`;

const Steps = styled.ol`
  background: #fbfaf7;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  color: ${theme.ink700};
  font-size: 0.88rem;
  line-height: 1.5;
  list-style: none;
  margin-bottom: 0.85rem;
  padding: 0.7rem 0.8rem;
`;

const Step = styled.li`
  display: flex;
  gap: 0.5rem;
  padding: 0.12rem 0;
  &::before {
    color: ${theme.goldDark};
    content: "${(props) => props.$n}";
    flex: 0 0 auto;
    font-weight: 800;
  }
`;

const Facts = styled.div`
  border: 1px solid ${theme.border};
  border-radius: 10px;
  margin-bottom: 0.85rem;
  overflow: hidden;
`;

const Fact = styled.div`
  align-items: flex-start;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  font-size: 0.88rem;
  gap: 0.5rem;
  padding: 0.5rem 0.7rem;
  &:last-child {
    border-bottom: 0;
  }
`;

const FactLabel = styled.span`
  color: ${theme.muted};
  flex: 0 0 88px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding-top: 0.12rem;
  text-transform: uppercase;
`;

const FactValue = styled.span`
  color: ${theme.ink};
  flex: 1 1 auto;
  font-weight: 700;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const OtaWarn = styled.div`
  align-items: flex-start;
  background: #fdf3e3;
  border: 1px solid #f0dfc0;
  border-radius: 8px;
  color: #8a5a00;
  display: flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 0.4rem;
  line-height: 1.45;
  margin-bottom: 0.85rem;
  padding: 0.55rem 0.7rem;
`;

const AckRow = styled.label`
  align-items: flex-start;
  background: #f4f2ee;
  border-radius: 8px;
  color: ${theme.ink700};
  cursor: pointer;
  display: flex;
  font-size: 0.85rem;
  font-weight: 700;
  gap: 0.55rem;
  line-height: 1.45;
  margin-bottom: 0.9rem;
  padding: 0.6rem 0.7rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  gap: 0.35rem;
  padding: 0.55rem 1rem;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Cancel = styled(Button)`
  background: #ffffff;
  border: 1px solid ${theme.border};
  color: ${theme.ink700};
`;

const Danger = styled(Button)`
  background: #b42318;
  border: 0;
  color: #ffffff;
`;

const VoidRefundDialog = (props) => {
  const { t } = useT();
  const [acknowledged, setAcknowledged] = useState(false);
  const info = props.info || {};

  return (
    <Backdrop onClick={props.onCancel}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Title>
          <AlertTriangle size={17} color="#b42318" aria-hidden="true" />
          {t("void_refund_title")}
        </Title>

        <Steps>
          <Step $n="1.">{t("void_refund_step_1")}</Step>
          <Step $n="2.">{t("void_refund_step_2")}</Step>
          <Step $n="3.">{t("void_refund_step_3")}</Step>
        </Steps>

        <Facts>
          <Fact>
            <FactLabel>{t("void_refund_guest")}</FactLabel>
            <FactValue>{props.guestName || "—"}</FactValue>
          </Fact>
          <Fact>
            <FactLabel>
              <Wallet size={11} aria-hidden="true" /> {t("void_refund_amount")}
            </FactLabel>
            <FactValue>{info.amount_formatted || "—"}</FactValue>
          </Fact>
          <Fact>
            <FactLabel>
              <Mail size={11} aria-hidden="true" /> {t("void_refund_email")}
            </FactLabel>
            <FactValue>{info.guest_email || "—"}</FactValue>
          </Fact>
          <Fact>
            <FactLabel>
              <Phone size={11} aria-hidden="true" /> {t("void_refund_phone")}
            </FactLabel>
            <FactValue>{info.recipient_phone || "—"}</FactValue>
          </Fact>
        </Facts>

        {info.guest_email_is_ota ? (
          <OtaWarn>
            <AlertTriangle size={14} aria-hidden="true" />
            <span>{t("void_refund_ota_warning")}</span>
          </OtaWarn>
        ) : null}

        <AckRow>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
          />
          {t("void_refund_ack")}
        </AckRow>

        <Actions>
          <Cancel type="button" onClick={props.onCancel}>
            <X size={15} /> {t("confirm_cancel")}
          </Cancel>
          <Danger
            type="button"
            disabled={!acknowledged || props.busy}
            onClick={props.onConfirm}
          >
            <Check size={15} /> {t("void_refund_ok")}
          </Danger>
        </Actions>
      </Card>
    </Backdrop>
  );
};

export default VoidRefundDialog;
